import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { ChartTooltip } from './ChartTooltip';
import { useChartTheme } from './useChartTheme';

export interface DistributionDatum {
  label: string;
  value: number;
}

export interface DistributionChartProps {
  data: readonly DistributionDatum[];
  height?: number;
  /** Name of the measure, shown in the tooltip. */
  measure: string;
  formatValue?: (value: number) => string;
  /** Highlights one bar — used for "this session" against the distribution. */
  highlightLabel?: string;
  horizontal?: boolean;
}

/**
 * A single-measure bar chart. One colour for the whole series: the bars are
 * the same kind of thing, so varying hue per bar would encode nothing.
 */
export function DistributionChart({
  data,
  height = 200,
  measure,
  formatValue,
  highlightLabel,
  horizontal = false,
}: DistributionChartProps) {
  const theme = useChartTheme();
  const base = theme.series[0] ?? theme.signal;
  const highlight = theme.series[2] ?? theme.signal;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data as DistributionDatum[]}
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={{ top: 8, right: 12, bottom: 0, left: horizontal ? 8 : -18 }}
        barCategoryGap={horizontal ? '24%' : '18%'}
      >
        <CartesianGrid stroke={theme.grid} vertical={horizontal} horizontal={!horizontal} />

        {horizontal ? (
          <>
            <XAxis
              type="number"
              tickLine={false}
              axisLine={false}
              tick={{ fill: theme.textMuted, fontSize: 11 }}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={104}
              tickLine={false}
              axisLine={false}
              tick={{ fill: theme.textMuted, fontSize: 11 }}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey="label"
              // Every band is a distinct category; dropping ticks to save room
              // would leave bars unlabelled, which colour alone cannot fix.
              interval={data.length > 12 ? 'preserveStartEnd' : 0}
              tickLine={false}
              axisLine={false}
              tick={{ fill: theme.textMuted, fontSize: 11 }}
              minTickGap={2}
            />
            <YAxis
              allowDecimals={false}
              width={46}
              tickLine={false}
              axisLine={false}
              tick={{ fill: theme.textMuted, fontSize: 11 }}
            />
          </>
        )}

        <Tooltip
          cursor={{ fill: theme.grid }}
          content={
            <ChartTooltip
              nameOf={() => measure}
              format={(value) =>
                formatValue && typeof value === 'number' ? formatValue(value) : String(value)
              }
            />
          }
        />

        <Bar dataKey="value" radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]} isAnimationActive={false}>
          {data.map((datum) => (
            <Cell
              key={datum.label}
              fill={highlightLabel === datum.label ? highlight : base}
              stroke={theme.surface}
              strokeWidth={2}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
